import React, { FC, useRef, useEffect } from 'react';
import Checkbox from '@components/ui/Checkbox';
import Button from '@components/ui/Button';
import { IStatement } from '@/features/workspace/types/banking';

interface StatementsTableProps {
  statements: IStatement[];
  selectedStatements: string[];
  onSelectStatement: (id: string) => void;
  onSelectAll: () => void;
  isSending: boolean;
  onDownload: () => void;
}

const StatementsTable: FC<StatementsTableProps> = ({
  statements,
  selectedStatements,
  onSelectStatement,
  onSelectAll,
  isSending,
  onDownload,
}) => {
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const selectedCount = selectedStatements.length;
  const totalCount = statements.length;
  const isAllSelected = totalCount > 0 && selectedCount === totalCount;
  const isIndeterminate = selectedCount > 0 && selectedCount < totalCount;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  return (
    <div className="w-full bg-white rounded-lg">
      <div className="grid grid-cols-[auto,1fr] items-center gap-4 border-b border-grey-200 bg-white px-6 py-3">
        <div className="flex items-center">
          <input
            ref={headerCheckboxRef}
            checked={isAllSelected}
            className="w-5 h-5 cursor-pointer accent-primary rounded border-grey-300"
            type="checkbox"
            onChange={onSelectAll}
          />
        </div>
        <div className="text-subtitle3a text-navy-700">Date</div>
      </div>
      <div>
        {statements.map((statement) => (
          <div
            key={statement.id}
            className="grid grid-cols-[auto,1fr] items-center gap-4 border-b border-grey-200 bg-white px-6 py-3 hover:bg-navy-50"
          >
            <Checkbox
              checked={selectedStatements.includes(statement.id)}
              onChange={() => onSelectStatement(statement.id)}
            />
            <div className="flex items-center gap-2 text-body3 text-navy-600">
              <i className="ri-file-text-line text-navy-400" />
              <span>{statement.filename}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between p-4 border-t border-grey-200">
        <div className="text-subtitle3a text-navy-700">
          {selectedStatements.length} Item
          {selectedStatements.length !== 1 ? 's' : ''} selected
        </div>
        <div>
          <Button
            disabled={selectedStatements.length === 0 || isSending}
            iconRight={<i className="ri-download-line text-lg" />}
            label="Download"
            loading={isSending}
            size="medium"
            variant="tertiary"
            onClick={onDownload}
          />
        </div>
      </div>
    </div>
  );
};

export default StatementsTable;
